import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, Crown, Star, Zap, AlertCircle, ArrowDown, Lock, Trash2, X } from 'lucide-react';
import { useSnackbar } from '@/context/snackbarContext';
import { billingApiService, SubscriptionInfo, SubscriptionChange } from '@/api/billingApi';
import { usePlans, FrontendPlanConfig } from '@/config/plans';
import { translateBackendMessage } from '../../lib/utils';


// Backend subscription plan interface (different from frontend config)
interface SubscriptionPlan {
    id: string;
    name: string;
    description: string;
    price: number;
    currency: string;
    interval: 'month' | 'year';
    features: string[];
    stripePriceId: string;
    dailyImageLimit: number;
    tier: 'starter' | 'pro' | 'platinum';
    isActive: boolean;
    sortOrder: number;
}

interface SubscriptionPlansModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentTier?: 'trial' | 'starter' | 'pro' | 'platinum';
    subscriptionInfo?: SubscriptionInfo;
    onSubscriptionChange?: () => void;
}

const SubscriptionPlansModal: React.FC<SubscriptionPlansModalProps> = ({
    isOpen,
    onClose,
    currentTier = 'trial',
    subscriptionInfo,
    onSubscriptionChange
}) => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const plans = usePlans();

    // Only show paid plans in the modal - trial is not a subscription choice
    const paidPlans = plans.filter(plan => plan.tier !== 'trial');

    const [showConfirmation, setShowConfirmation] = useState<{
        type: 'cancel' | 'downgrade';
        targetTier?: 'starter' | 'pro';
    } | null>(null);

    // Fetch pending subscription changes (single request when modal opens)
    const { data: pendingChangesData } = useQuery({
        queryKey: ['pending-subscription-changes'],
        queryFn: billingApiService.getPendingSubscriptionChanges,
        enabled: isOpen && !!subscriptionInfo,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: 0,
    });

    const pendingChanges = pendingChangesData?.subscriptionChanges || [];
    const hasPendingChanges = pendingChanges.length > 0;

    const nextPendingChange: SubscriptionChange | undefined = useMemo(() => {
        if (!pendingChanges.length) return undefined;
        const sorted = [...pendingChanges].sort((a, b) => {
            const aTime = a.scheduledDate ?? a.effectiveDate ?? a.createdAt;
            const bTime = b.scheduledDate ?? b.effectiveDate ?? b.createdAt;
            return aTime - bTime;
        });
        return sorted[0];
    }, [pendingChanges]);

    const cancelChangeMutation = useMutation({
        mutationFn: ({ subscriptionChangeId }: { subscriptionChangeId: string }) =>
            billingApiService.cancelSubscriptionChange(subscriptionChangeId),
        onSuccess: (data) => {
            showSnackbar(
                translateBackendMessage(data.message, t) || t('subscription.changeCancelledSuccess'),
                'success'
            );
            queryClient.invalidateQueries({ queryKey: ['subscription'] });
            queryClient.invalidateQueries({ queryKey: ['userLimits'] });
            queryClient.invalidateQueries({ queryKey: ['pending-subscription-changes'] });
            queryClient.invalidateQueries({ queryKey: ['subscriptionInfo'] });
        },
        onError: (error: any) => {
            showSnackbar(
                translateBackendMessage(error.response?.data?.message, t) || t('subscription.changeCancelledError'),
                'error'
            );
        },
    });

    // Note: We intentionally avoid extra refetch effects to prevent duplicate calls.

    const { data: plansData, isLoading, error } = useQuery({
        queryKey: ['subscription-plans'],
        queryFn: billingApiService.getSubscriptionPlans,
    });

    // Cancel subscription mutation
    const cancelMutation = useMutation({
        mutationFn: billingApiService.cancelSubscription,
        onSuccess: (data) => {
            showSnackbar(
                translateBackendMessage(data.message, t) || t('subscription.cancelSuccess'),
                'success'
            );
            queryClient.invalidateQueries({ queryKey: ['subscription'] });
            queryClient.invalidateQueries({ queryKey: ['userLimits'] });
            queryClient.invalidateQueries({ queryKey: ['pending-subscription-changes'] });
            setShowConfirmation(null);
            onClose();
            if (onSubscriptionChange) {
                onSubscriptionChange();
            }
        },
        onError: (error: any) => {
            showSnackbar(
                translateBackendMessage(error.response?.data?.message, t) || t('subscription.cancelError'),
                'error'
            );
        },
    });

    // Downgrade subscription mutation
    const downgradeMutation = useMutation({
        mutationFn: ({ targetTier }: { targetTier: 'starter' | 'pro' }) =>
            billingApiService.downgradeSubscription(targetTier),
        onSuccess: (data) => {
            // Get the plan name for interpolation
            const targetPlan = plans.find(p => p.tier === data.subscription?.targetTier);
            const planName = targetPlan?.name || data.subscription?.targetTier || 'Unknown Plan';

            showSnackbar(
                translateBackendMessage(data.message, t, { planName }) || t('subscription.downgradeSuccess'),
                'success'
            );
            queryClient.invalidateQueries({ queryKey: ['subscription'] });
            queryClient.invalidateQueries({ queryKey: ['userLimits'] });
            queryClient.invalidateQueries({ queryKey: ['pending-subscription-changes'] });
            setShowConfirmation(null);
        },
        onError: (error: any) => {
            showSnackbar(
                translateBackendMessage(error.response?.data?.message, t) || t('subscription.downgradeError'),
                'error'
            );
        },
    });

    const handleSubscribe = async (plan: SubscriptionPlan) => {
        // Prevent subscription if there are pending changes
        if (hasPendingChanges) {
            showSnackbar(
                'You have pending subscription changes. Please wait for them to be processed.',
                'warning'
            );
            return;
        }

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                showSnackbar('Please log in to subscribe to a plan', 'error');
                return;
            }

            const response = await billingApiService.createCheckoutSession(plan.stripePriceId);

            if (response.success && response.session?.url) {
                // Redirect to Stripe checkout
                window.location.href = response.session.url;
            } else {
                const errorMessage = response.message || 'Failed to create checkout session. Please try again.';
                showSnackbar(errorMessage, 'error');
            }
        } catch (error) {
            console.error('Failed to create checkout session:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to start checkout. Please try again.';
            showSnackbar(errorMessage, 'error');
        }
    };



    const formatPrice = (price: number, currency: string) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency.toUpperCase(),
        }).format(price / 100);
    };

    const getTierIcon = (tier: string) => {
        switch (tier) {
            case 'unlimited':
                return <Crown className="h-5 w-5" />;
            case 'premium':
                return <Star className="h-5 w-5" />;
            default:
                return <Zap className="h-5 w-5" />;
        }
    };

    const handleConfirmAction = () => {
        if (!showConfirmation) return;

        // Prevent action if there are pending changes
        if (hasPendingChanges) {
            showSnackbar(
                t('subscription.pendingChangesBlockedMessage'),
                'warning'
            );
            return;
        }

        switch (showConfirmation.type) {
            case 'cancel':
                cancelMutation.mutate(false); // false = cancel at period end
                break;
            case 'downgrade':
                if (showConfirmation.targetTier) {
                    downgradeMutation.mutate({ targetTier: showConfirmation.targetTier });
                }
                break;
        }
    };

    const getActionForPlan = (planTier: 'trial' | 'starter' | 'pro' | 'platinum') => {
        // Trial users can only upgrade, never downgrade
        if (currentTier === 'trial') {
            return 'upgrade';
        }

        if (planTier === currentTier) {
            return 'current';
        }

        // Tier hierarchy: trial < starter < pro < platinum
        const tierHierarchy = { 'trial': 0, 'starter': 1, 'pro': 2, 'platinum': 3 };
        const currentLevel = tierHierarchy[currentTier as keyof typeof tierHierarchy] || 0;
        const planLevel = tierHierarchy[planTier as keyof typeof tierHierarchy] || 0;

        if (planLevel > currentLevel) {
            return 'upgrade';
        } else if (planLevel < currentLevel) {
            return 'downgrade';
        }

        return 'current';
    };

    const canDowngradeTo = (targetTier: 'starter' | 'pro') => {
        if (currentTier === 'trial') return false; // Trial users can't downgrade
        if (currentTier === 'starter') return false; // Starter is the lowest tier
        if (currentTier === 'pro' && targetTier === 'pro') return false;
        // Platinum users can downgrade to pro or starter
        if (currentTier === 'platinum') return true;
        // Pro users can only downgrade to starter
        if (currentTier === 'pro' && targetTier === 'starter') return true;
        return false;
    };

    const isProcessing = cancelMutation.isPending || downgradeMutation.isPending;

    if (isLoading) {
        return (
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="max-w-4xl">
                    <div className="flex items-center justify-center p-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500" />
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    if (error) {
        return (
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="max-w-4xl">
                    <div className="text-center p-8">
                        <p className="text-red-500">{t('common.error')}</p>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    // Get server plans for Stripe integration, fallback to local config
    const serverPlans = (plansData as { plans: SubscriptionPlan[] })?.plans || [];

    return (
        <>
            {/* Main Subscription Plans Modal */}
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent
                    className="max-w-6xl max-h-[90vh] overflow-y-auto p-4 sm:p-6"
                    onTouchStart={(e) => {
                        const touch = e.touches[0];
                        const startY = touch.clientY;
                        const startTime = Date.now();

                        const handleTouchMove = (e: TouchEvent) => {
                            const touch = e.touches[0];
                            const currentY = touch.clientY;
                            const deltaY = currentY - startY;

                            // Only allow downward swipe
                            if (deltaY > 0) {
                                e.preventDefault();
                            }
                        };

                        const handleTouchEnd = (e: TouchEvent) => {
                            const touch = e.changedTouches[0];
                            const endY = touch.clientY;
                            const endTime = Date.now();
                            const deltaY = endY - startY;
                            const deltaTime = endTime - startTime;

                            // Close if swiped down more than 100px and took less than 500ms
                            if (deltaY > 100 && deltaTime < 500) {
                                onClose();
                            }

                            document.removeEventListener('touchmove', handleTouchMove);
                            document.removeEventListener('touchend', handleTouchEnd);
                        };

                        document.addEventListener('touchmove', handleTouchMove, { passive: false });
                        document.addEventListener('touchend', handleTouchEnd);
                    }}
                >
                    <DialogHeader className="text-center pb-4 sm:pb-6">
                        <DialogTitle className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                            {hasPendingChanges ? t('subscription.pendingSubscriptionChange') : t('subscription.upgradeExperience')}
                        </DialogTitle>
                        <p className="text-muted-foreground text-base sm:text-lg mt-2">
                            {hasPendingChanges
                                ? t('subscription.pendingChangeResolve')
                                : t('subscription.choosePerfectPlan')
                            }
                        </p>
                    </DialogHeader>

                    {/* Concise pending change banner */}
                    {hasPendingChanges && (
                        <div className="mb-6">
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 sm:p-4">
                                <div className="flex items-start gap-2 sm:gap-3">
                                    <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs sm:text-sm text-orange-900 leading-relaxed break-words">
                                            {(() => {
                                                const dateStr = new Date(
                                                    (nextPendingChange?.scheduledDate ?? nextPendingChange?.effectiveDate ?? subscriptionInfo?.currentPeriodEnd ?? Date.now())
                                                ).toLocaleDateString();
                                                if (nextPendingChange) {
                                                    const fromPlan = plans.find(p => p.tier === nextPendingChange.fromTier)?.name || nextPendingChange.fromTier;
                                                    const toPlan = plans.find(p => p.tier === nextPendingChange.toTier)?.name || nextPendingChange.toTier;
                                                    switch (nextPendingChange.changeType) {
                                                        case 'downgrade':
                                                            return t('subscription.pendingDowngradeDescription', { fromPlan, toPlan, date: dateStr });
                                                        case 'upgrade':
                                                            return t('subscription.pendingUpgradeDescription', { fromPlan, toPlan, date: dateStr });
                                                        case 'cancel':
                                                            return t('subscription.pendingCancelDescription', { fromPlan, date: dateStr });
                                                        default:
                                                            return t('subscription.pendingChangeDescription');
                                                    }
                                                }
                                                if (subscriptionInfo?.currentPeriodEnd) {
                                                    const fromPlanName = plans.find(p => p.tier === currentTier)?.name || currentTier;
                                                    return t('subscription.pendingCancelDescription', { fromPlan: fromPlanName, date: new Date(subscriptionInfo.currentPeriodEnd).toLocaleDateString() });
                                                }
                                                return t('subscription.pendingChangesDescription');
                                            })()}
                                        </div>
                                    </div>
                                    {nextPendingChange && (
                                        <div className="flex-shrink-0">
                                            {/* Mobile: Icon only button */}
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="sm:hidden border-orange-300 text-orange-700 hover:bg-orange-100 p-2"
                                                disabled={cancelChangeMutation.isPending}
                                                onClick={() => cancelChangeMutation.mutate({ subscriptionChangeId: nextPendingChange.id })}
                                                title={t('subscription.cancelPendingChange')}
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                            {/* Desktop: Full text button */}
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="hidden sm:flex border-orange-300 text-orange-700 hover:bg-orange-100"
                                                disabled={cancelChangeMutation.isPending}
                                                onClick={() => cancelChangeMutation.mutate({ subscriptionChangeId: nextPendingChange.id })}
                                            >
                                                <X className="h-4 w-4 mr-1" /> {t('subscription.cancelPendingChange')}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Show subscription plans */}
                    <div className="grid gap-4 sm:gap-6 md:grid-cols-3">
                        {/* Only show paid plans - trial is not a subscription choice */}
                        {paidPlans.map((plan: FrontendPlanConfig) => {
                            // Find matching server plan for Stripe integration (only for paid plans)
                            const serverPlan = plan.tier !== 'trial'
                                ? serverPlans.find(sp => sp.tier === plan.tier)
                                : null;

                            const action = getActionForPlan(plan.tier);
                            // Only disable actions when there are pending changes or when processing
                            const isActionDisabled = hasPendingChanges || isProcessing;

                            return (
                                <Card key={plan.id} className={`relative ${currentTier === plan.tier ? 'ring-2 ring-blue-500' : ''}`}>
                                    {plan.popular && (
                                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                                            <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                                                {t('subscription.mostPopular')}
                                            </Badge>
                                        </div>
                                    )}
                                    {currentTier === plan.tier && (
                                        <div className={`absolute -top-3 z-10 ${plan.popular ? 'right-2 sm:right-4' : 'right-4'}`}>
                                            <Badge className="bg-blue-500 text-white">
                                                {t('subscription.current')}
                                            </Badge>
                                        </div>
                                    )}
                                    <CardHeader className="text-center p-4 sm:p-6">
                                        <div className="flex justify-center mb-2">
                                            {getTierIcon(plan.tier)}
                                        </div>
                                        <CardTitle className="text-lg sm:text-xl">{plan.name}</CardTitle>
                                        <CardDescription className="text-sm">{plan.description}</CardDescription>
                                    </CardHeader>
                                    <CardContent className="p-4 sm:p-6">
                                        <div className="text-center mb-4 sm:mb-6">
                                            <span className="text-3xl sm:text-4xl font-bold">
                                                {formatPrice(plan.price, plan.currency)}
                                            </span>
                                            <span className="text-muted-foreground text-sm sm:text-base">/{plan.interval}</span>
                                        </div>
                                        <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                                            {plan.features.map((feature: any, index: number) => (
                                                <li key={index} className="flex items-start">
                                                    <Check className="h-4 w-4 text-green-500 mr-2 sm:mr-3 mt-0.5 flex-shrink-0" />
                                                    <span className="text-xs sm:text-sm">{feature.text}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        {/* Action Buttons */}
                                        <div className="space-y-2 sm:space-y-3">
                                            {action === 'current' && (
                                                <Button disabled className="w-full" variant="outline">
                                                    {t('subscription.currentPlan')}
                                                </Button>
                                            )}

                                            {action === 'upgrade' && (
                                                <Button
                                                    onClick={() => serverPlan && handleSubscribe(serverPlan)}
                                                    className={`w-full ${plan.tier === 'platinum'
                                                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'
                                                        : 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600'
                                                        } text-white font-semibold`}
                                                    disabled={!serverPlan || isActionDisabled}
                                                >
                                                    {hasPendingChanges ? (
                                                        <>
                                                            <Lock className="h-4 w-4 mr-2" />
                                                            {t('subscription.pendingChangesBlocked')}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Zap className="h-4 w-4 mr-2" />
                                                            {t('subscription.upgradeTo', { plan: plan.name })}
                                                        </>
                                                    )}
                                                </Button>
                                            )}

                                            {action === 'downgrade' && (
                                                <Button
                                                    onClick={() => {
                                                        setShowConfirmation({
                                                            type: 'downgrade',
                                                            targetTier: plan.tier as 'starter' | 'pro'
                                                        });
                                                    }}
                                                    variant="outline"
                                                    className="w-full border-orange-300 text-orange-600 hover:bg-orange-50"
                                                    disabled={!canDowngradeTo(plan.tier as 'starter' | 'pro') || isActionDisabled}
                                                >
                                                    {hasPendingChanges ? (
                                                        <>
                                                            <Lock className="h-4 w-4 mr-2" />
                                                            Pending Changes
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ArrowDown className="h-4 w-4 mr-2" />
                                                            {t('subscription.downgradeTo', { plan: plan.name })}
                                                        </>
                                                    )}
                                                </Button>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>

                    {/* Add Cancel Subscription Option */}
                    {currentTier !== 'trial' && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                            <h4 className="font-semibold text-red-900 mb-2">{t('subscription.cancelTitle')}</h4>
                            <p className="text-sm text-red-700 mb-3">
                                {t('subscription.cancelHelp')}
                            </p>
                            <Button
                                onClick={() => {
                                    setShowConfirmation({
                                        type: 'cancel'
                                    });
                                }}
                                variant="outline"
                                className="border-red-300 text-red-600 hover:bg-red-50"
                                disabled={isProcessing}
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                {t('subscription.cancel')}
                            </Button>
                        </div>
                    )}

                    <div className="mt-8 text-center">
                        <p className="text-sm text-muted-foreground">
                            {t('subscription.securePaymentGuarantee')}
                        </p>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Confirmation Dialog - Separate Modal */}
            <Dialog open={!!showConfirmation} onOpenChange={() => setShowConfirmation(null)}>
                <DialogContent
                    className="max-w-md"
                    onTouchStart={(e) => {
                        const touch = e.touches[0];
                        const startY = touch.clientY;
                        const startTime = Date.now();

                        const handleTouchMove = (e: TouchEvent) => {
                            const touch = e.touches[0];
                            const currentY = touch.clientY;
                            const deltaY = currentY - startY;

                            // Only allow downward swipe
                            if (deltaY > 0) {
                                e.preventDefault();
                            }
                        };

                        const handleTouchEnd = (e: TouchEvent) => {
                            const touch = e.changedTouches[0];
                            const endY = touch.clientY;
                            const endTime = Date.now();
                            const deltaY = endY - startY;
                            const deltaTime = endTime - startTime;

                            // Close if swiped down more than 100px and took less than 500ms
                            if (deltaY > 100 && deltaTime < 500) {
                                setShowConfirmation(null);
                            }

                            document.removeEventListener('touchmove', handleTouchMove);
                            document.removeEventListener('touchend', handleTouchEnd);
                        };

                        document.addEventListener('touchmove', handleTouchMove, { passive: false });
                        document.addEventListener('touchend', handleTouchEnd);
                    }}
                >
                    <DialogHeader className="text-left">
                        <DialogTitle className="flex items-center gap-1">
                            <AlertCircle className="w-5 h-5 text-orange-500" />
                            {showConfirmation?.type === 'cancel' && t('subscription.confirmCancel')}
                            {showConfirmation?.type === 'downgrade' &&
                                t('subscription.confirmDowngrade', { tier: showConfirmation.targetTier })
                            }
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            {showConfirmation?.type === 'cancel' &&
                                t('subscription.cancelDescription', {
                                    date: subscriptionInfo?.currentPeriodEnd
                                        ? new Date(subscriptionInfo.currentPeriodEnd * 1000).toLocaleDateString()
                                        : 'the end of your billing period'
                                })
                            }
                            {showConfirmation?.type === 'downgrade' &&
                                t('subscription.downgradeDescription', { tier: showConfirmation.targetTier })
                            }
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2 justify-end">
                            <Button
                                onClick={() => setShowConfirmation(null)}
                                disabled={isProcessing}
                                variant="outline"
                                size="sm"
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                onClick={handleConfirmAction}
                                disabled={isProcessing || hasPendingChanges}
                                variant="destructive"
                                size="sm"
                            >
                                {isProcessing ? t('common.processing') : t('common.confirm')}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default SubscriptionPlansModal;
